import {
  ApolloClient, ApolloLink, createHttpLink, InMemoryCache,
  split,
} from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { getSubscriptionTokenFromCookie } from '@teamango/core';
import { createClient } from 'graphql-ws';
import {
  apolloErrorLink,
  apolloOriginIdLink,
  apolloRequestIdLink,
} from './links.ts';
import { markWsClosed, markWsConnected } from './ws-connection.ts';

const wsLink = new GraphQLWsLink(createClient({
  url: import.meta.env.VITE_TEAMANGO_API_WS_URL,
  connectionParams: () => {
    // Subscription token is stored in a non-httpOnly cookie for JS access
    const subscriptionToken = getSubscriptionTokenFromCookie();

    return {
      authorization: subscriptionToken ? `Bearer ${subscriptionToken}` : undefined,
    };
  },
  keepAlive: 10_000, // ping server every 10 seconds
  // Publish socket liveness as module state: subscriptions carry no backlog, so
  // a reconnect means "you may have missed events" and screens re-pull on it.
  // See `ws-connection.ts` and `useRevalidateOnResume`.
  on: {
    connected: markWsConnected,
    closed: markWsClosed,
  },
}));

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_TEAMANGO_API_GQL_URL,
  credentials: 'include', // Send cookies with requests
});

const addQueryNameLink = new ApolloLink((operation, forward) => {
  const queryName = operation.operationName;

  operation.setContext(() => ({
    uri: `${import.meta.env.VITE_TEAMANGO_API_GQL_URL}?operation=${queryName}`,
  }));

  return forward(operation);
});

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return (
      definition.kind === 'OperationDefinition'
      && definition.operation === 'subscription'
    );
  },
  wsLink,
  // No need for apolloAuthLink (cookies sent automatically) or apolloRefreshTokenLink (server handles refresh)
  apolloRequestIdLink
    .concat(apolloOriginIdLink)
    .concat(addQueryNameLink)
    .concat(httpLink),
);

export const apolloClient = new ApolloClient({
  link: apolloErrorLink.concat(splitLink),
  cache: new InMemoryCache({
    possibleTypes: {
      Broadcast: [
        'EmailBroadcast',
        'SmsBroadcast',
      ],
      Document: [
        'CertificateOfStudyDocument',
        'CallCertificateDocument',
        'ApplicationForAbsenceDocument',
        'ApplicationForAcademicLeaveDocument',
        'ApplicationForCancelAccrualDocument',
        'ApplicationForCourseCreditTransferDocument',
        'ApplicationForCreditRetakeDocument',
        'ApplicationForDeadlineExtensionDocument',
        'ApplicationForExpulsionDocument',
        'ApplicationForIndividualEducationDocument',
        'ApplicationForLastNameChangeDocument',
        'ApplicationForOnlineEducationDocument',
        'ApplicationForReinstatementDocument',
        'ApplicationForTransferDocument',
      ],
    },
    typePolicies: {
      Query: {
        fields: {
          groupMembersConnection: relayStylePagination(),
          organizationMembersConnection: relayStylePagination(),
          chatMessages: relayStylePagination(['chatId']),
          studentEvents: relayStylePagination(['studentId', 'filter']),
        },
      },
      // A LinkedIssue is one relation row read from one END: the same `id`
      // is BLOCKED_BY + the blocker on one issue and BLOCKS + the blocked issue
      // on the other. Normalized by that id, opening one end rewrote the entry
      // the other end's `relations` points at, so an issue page reached from
      // the cache rendered the other issue's reading of its relations. Kept
      // inside the issue that owns the list instead.
      LinkedIssue: {
        keyFields: false,
      },
      Issue: {
        fields: {
          // The server sends the whole list every time — replace, don't merge.
          relations: {
            merge: false,
          },
        },
      },
    },
  }),
});
